import React from "react";

import { Error } from "@mui/icons-material";
import {
  Box,
  FormLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";

interface StartCoursewareMigrationFormProps {
  regionCode: string;
}

export const StartCoursewareMigrationForm: React.FC<
  StartCoursewareMigrationFormProps
> = ({ regionCode }) => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { coursewareMigrationFormState, setCoursewareMigrationFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.warning.main}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.warning.main}
          data-testid="migrate-courseware-info-panel"
        >
          <Grid container alignItems="center">
            <Grid item xs={2} container justifyContent="center">
              <Error fontSize="large" />
            </Grid>
            <Grid item xs={10} style={{ paddingRight: "20px" }}>
              <ol>
                <li>
                  <Typography>
                    You are about to start a Courseware Migration.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    This process will copy all courses from the source EdApp
                    application to the EdApp application linked to this
                    organization.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <b>Warning:</b> This workflow will remove all roles from all
                    users of the source application, effectively deactivating
                    their access to the source EdApp application.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <b>Source Application ID</b> must be provided to identify
                    the EdApp application to copy courses from.
                  </Typography>
                </li>
              </ol>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required disabled>
            Organisation ID
          </FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required disabled>
            Region
          </FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Region"
            inputProps={{
              "data-testid": "input-region",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={regionCode}
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel required>Source Application ID</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Source Application ID"
            placeholder="Enter Source EdApp Application ID"
            inputProps={{
              "data-testid": "input-source-application-id",
            }}
            error={coursewareMigrationFormState.sourceApplicationId === ""}
            fullWidth
            margin="normal"
            variant="outlined"
            value={coursewareMigrationFormState.sourceApplicationId}
            onChange={(e) =>
              setCoursewareMigrationFormState({
                ...coursewareMigrationFormState,
                sourceApplicationId: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Organisation Name</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Organisation Name"
            placeholder="Enter organisation name (optional)"
            inputProps={{
              "data-testid": "input-org-name",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={coursewareMigrationFormState.orgName}
            onChange={(e) =>
              setCoursewareMigrationFormState({
                ...coursewareMigrationFormState,
                orgName: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center">
        <Grid item xs={5}>
          <FormLabel>Admin Email</FormLabel>
        </Grid>

        <Grid item xs={7}>
          <TextField
            name="Admin Email"
            type="email"
            placeholder="Enter admin email (optional)"
            inputProps={{
              "data-testid": "input-admin-email",
              autoComplete: "off",
            }}
            fullWidth
            margin="normal"
            variant="outlined"
            value={coursewareMigrationFormState.adminEmail}
            onChange={(e) =>
              setCoursewareMigrationFormState({
                ...coursewareMigrationFormState,
                adminEmail: e.target.value,
                adminPassword: e.target.value
                  ? coursewareMigrationFormState.adminPassword
                  : "",
              })
            }
          />
        </Grid>
      </Grid>

      {coursewareMigrationFormState.adminEmail && (
        <Grid container direction="row" alignItems="center">
          <Grid item xs={5}>
            <FormLabel>Admin Password</FormLabel>
          </Grid>

          <Grid item xs={7}>
            <TextField
              name="Admin Password"
              type="password"
              placeholder="Enter admin password (optional)"
              inputProps={{
                "data-testid": "input-admin-password",
                autoComplete: "off",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={coursewareMigrationFormState.adminPassword}
              onChange={(e) =>
                setCoursewareMigrationFormState({
                  ...coursewareMigrationFormState,
                  adminPassword: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
